/**
 * Dettaglio documento commerciale (Fase 2.3).
 * Testata + controparte + pagamento + righe + risalita catena
 * (ordine/preventivo/DDT di origine). Il pagamento con eventuale movimento di
 * cassa e' gestito in Fase 2.3b. UI solo da @floraltech/ui.
 */
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@floraltech/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface Riga {
  id: string;
  descrizione: string;
  quantita: number | string;
  prezzo_unitario: number | string;
  iva_perc: number | string | null;
  importo_riga: number | string;
}

interface Classificazione {
  id: string;
  codice: string;
  descrizione: string;
}

interface Centro {
  id: string;
  codice: string;
  nome: string;
}

interface Documento {
  id: string;
  tipo: string;
  numero: string;
  anno: number;
  direzione: string;
  stato: string;
  data_documento: string;
  causale: string | null;
  controparte_denominazione: string | null;
  controparte_piva: string | null;
  ordine_id: string | null;
  ordine_tipo: string | null;
  preventivo_id: string | null;
  ddt_id: string | null;
  valuta: string;
  importo_totale: number | string;
  stato_pagamento: string;
  metodo_pagamento: string | null;
  importo_pagato: number | string;
  data_pagamento: string | null;
  centro_costo_id: string | null;
  note: string | null;
  righe: Riga[];
}

const fmtEUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

export function DocumentoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<Documento | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metodi, setMetodi] = useState<Classificazione[]>([]);
  const [centri, setCentri] = useState<Centro[]>([]);
  const [payMetodo, setPayMetodo] = useState('contanti');
  const [payImporto, setPayImporto] = useState('');
  const [payData, setPayData] = useState(new Date().toISOString().slice(0, 10));
  const [payBusy, setPayBusy] = useState(false);

  const load = async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/v1/documenti-commerciali/${id}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data = await res.json();
      setDoc(data);
      if (!payImporto) setPayImporto(String(data.importo_totale ?? ''));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    void (async () => {
      const res = await fetch('/api/v1/classificazioni?sistema=metodo_pagamento', {
        headers: authHeaders(),
      });
      if (res.ok) setMetodi(await res.json());
    })();
    void (async () => {
      const res = await fetch('/api/v1/centri-costo/centri?limit=200', {
        headers: authHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setCentri(Array.isArray(d) ? d : (d.items ?? []));
      }
    })();
  }, []);

  const centroLabel = (cid: string | null): string => {
    if (!cid) return '-';
    const c = centri.find((x) => x.id === cid);
    return c ? `${c.codice} - ${c.nome}` : '-';
  };

  const generaFattura = async () => {
    if (!id) return;
    setPayBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/documenti-commerciali/${id}/genera-fattura`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/documentale/fatture/${data.fattura_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione fattura');
    } finally {
      setPayBusy(false);
    }
  };

  const registraPagamento = async () => {
    if (!id) return;
    setPayBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/documenti-commerciali/${id}/pagamento`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metodo: payMetodo,
          importo: Number(payImporto) || 0,
          data: payData,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore registrazione pagamento');
    } finally {
      setPayBusy(false);
    }
  };

  if (error && !doc) return <p className="p-6 text-sm text-destructive">{error}</p>;
  if (!doc) return <p className="p-6 text-sm text-muted-foreground">Caricamento...</p>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold">Documento {doc.numero}</h1>
        <span className="rounded bg-neutral-200 px-2 py-1 text-xs dark:bg-neutral-700">
          {doc.tipo}
        </span>
        <span className="rounded bg-neutral-200 px-2 py-1 text-xs dark:bg-neutral-700">
          {doc.direzione}
        </span>
        <span className="rounded bg-neutral-200 px-2 py-1 text-xs dark:bg-neutral-700">
          {doc.stato_pagamento}
        </span>
        {doc.direzione === 'emesso' && (
          <Button size="sm" variant="secondary" onClick={generaFattura} disabled={payBusy}>
            {payBusy ? 'Generazione...' : 'Genera Fattura'}
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>{doc.direzione === 'emesso' ? 'Cliente' : 'Fornitore'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div>
              <p className="text-muted-foreground">Denominazione</p>
              <p>{doc.controparte_denominazione ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">P.IVA</p>
              <p>{doc.controparte_piva ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Data</p>
              <p>{doc.data_documento}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Causale</p>
              <p>{doc.causale ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Totale</p>
              <p className="font-medium">{fmtEUR.format(Number(doc.importo_totale))}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Centro di costo</p>
              <p>{centroLabel(doc.centro_costo_id)}</p>
            </div>
            {doc.ordine_id && (
              <div>
                <p className="text-muted-foreground">Ordine di origine</p>
                {doc.ordine_tipo === 'vendita' ? (
                  <button
                    className="text-sm text-blue-700 hover:underline"
                    onClick={() => navigate(`/ordini-vendita/${doc.ordine_id}`)}
                  >
                    Apri ordine vendita
                  </button>
                ) : (
                  <p>Ordine acquisto</p>
                )}
              </div>
            )}
            {doc.preventivo_id && (
              <div>
                <p className="text-muted-foreground">Preventivo</p>
                <button
                  className="text-sm text-blue-700 hover:underline"
                  onClick={() => navigate(`/preventivi/${doc.preventivo_id}`)}
                >
                  Apri preventivo
                </button>
              </div>
            )}
            {doc.ddt_id && (
              <div>
                <p className="text-muted-foreground">DDT collegato</p>
                <button
                  className="text-sm text-blue-700 hover:underline"
                  onClick={() => navigate(`/ddt/${doc.ddt_id}`)}
                >
                  Apri DDT
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pagamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Stato</p>
              <p>{doc.stato_pagamento}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Metodo</p>
              <p>{doc.metodo_pagamento ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Importo pagato</p>
              <p>{fmtEUR.format(Number(doc.importo_pagato))}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Data pagamento</p>
              <p>{doc.data_pagamento ?? '-'}</p>
            </div>
          </div>

          {doc.stato_pagamento !== 'pagato' && (
            <div className="mt-4 grid grid-cols-1 gap-3 border-t pt-4 md:grid-cols-4">
              <div>
                <Label>Metodo</Label>
                <select
                  className="w-full rounded border bg-transparent px-3 py-2 text-sm"
                  value={payMetodo}
                  onChange={(e) => setPayMetodo(e.target.value)}
                >
                  {metodi.map((m) => (
                    <option key={m.id} value={m.codice}>
                      {m.descrizione}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Importo</Label>
                <Input
                  type="number"
                  value={payImporto}
                  onChange={(e) => setPayImporto(e.target.value)}
                />
              </div>
              <div>
                <Label>Data</Label>
                <Input type="date" value={payData} onChange={(e) => setPayData(e.target.value)} />
              </div>
              <div className="flex items-end">
                <Button size="sm" onClick={registraPagamento} disabled={payBusy}>
                  {payBusy ? 'Registrazione...' : 'Registra pagamento'}
                </Button>
              </div>
            </div>
          )}
          {doc.stato_pagamento === 'pagato' && doc.metodo_pagamento === 'contanti' && (
            <p className="mt-3 text-xs text-muted-foreground">
              Pagato in contanti: movimento di cassa registrato.
            </p>
          )}
        </CardContent>
      </Card>

      {doc.righe.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Righe</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="pb-2">Descrizione</th>
                  <th className="pb-2">Qta</th>
                  <th className="pb-2">Prezzo</th>
                  <th className="pb-2">IVA%</th>
                  <th className="pb-2">Importo</th>
                </tr>
              </thead>
              <tbody>
                {doc.righe.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-1">{r.descrizione}</td>
                    <td className="py-1">{String(r.quantita)}</td>
                    <td className="py-1">{fmtEUR.format(Number(r.prezzo_unitario))}</td>
                    <td className="py-1">{r.iva_perc != null ? `${r.iva_perc}%` : '-'}</td>
                    <td className="py-1">{fmtEUR.format(Number(r.importo_riga))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default DocumentoDetail;
